import React, { useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { LinearProgress } from '@material-ui/core';
import { AiOutlineDownload, AiOutlinePrinter } from 'react-icons/ai';
import { toast } from 'react-toastify';
import saveAs from 'file-saver';

import { useAlteracaoPrecoEntradaFornecedor } from '../../../AlteracaoPrecoEntradaFornecedorContext';
import { LeitorPDF } from '../LeitorPDF';
import { ContainerPdf, TransmitModal } from './styles';

const PDF_MIME_TYPE = 'application/pdf';

const progressContainerStyle: React.CSSProperties = {
  height: 5,
};

const progressStyle: React.CSSProperties = {
  background: '#c2a0f8',
  color: '#8850BF',
};

const headerStyle: React.CSSProperties = {
  maxHeight: 50,
};

const footerStyle: React.CSSProperties = {
  marginTop: 30,
};

const normalizePdfBuffer = (data: unknown): ArrayBuffer => {
  if (data instanceof ArrayBuffer) {
    return data;
  }

  if (ArrayBuffer.isView(data)) {
    const view = data as ArrayBufferView;
    const copied = new Uint8Array(view.byteLength);

    copied.set(new Uint8Array(view.buffer, view.byteOffset, view.byteLength));

    return copied.buffer;
  }

  if (Array.isArray(data)) {
    return new Uint8Array(data).buffer;
  }

  throw new Error('Formato do PDF inválido.');
};

const getFormattedDate = (): string => {
  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const month = String(currentDate.getMonth() + 1).padStart(2, '0');
  const day = String(currentDate.getDate()).padStart(2, '0');

  return `${year}_${month}_${day}`;
};

export const ModalPDF: React.FC = () => {
  const { pdf, loadingPDF, isOpenModal, setIsOpenModal } =
    useAlteracaoPrecoEntradaFornecedor();

  const hasPdf = Boolean(pdf?.data);

  const handleCloseModal = useCallback((): void => {
    setIsOpenModal(false);
  }, [setIsOpenModal]);

  const createPdfBlob = useCallback((): Blob => {
    if (!pdf?.data) {
      throw new Error('O PDF não está disponível no momento.');
    }

    const buffer = normalizePdfBuffer(pdf.data);

    return new Blob([buffer], { type: PDF_MIME_TYPE });
  }, [pdf]);

  const handlePrint = useCallback((): void => {
    try {
      const blob = createPdfBlob();
      const fileUrl = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');

      iframe.style.position = 'fixed';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.opacity = '0';
      iframe.title = 'Impressão do PDF';
      iframe.src = fileUrl;

      iframe.onload = () => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();

        window.setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }

          URL.revokeObjectURL(fileUrl);
        }, 1000);
      };

      document.body.appendChild(iframe);
    } catch (error) {
      toast.error((error as Error).message);
    }
  }, [createPdfBlob]);

  const handleDownload = useCallback((): void => {
    try {
      const blob = createPdfBlob();

      saveAs(blob, `danfe_${getFormattedDate()}.pdf`);
    } catch (error) {
      toast.error((error as Error).message);
    }
  }, [createPdfBlob]);

  return (
    <TransmitModal
      show={isOpenModal}
      onHide={handleCloseModal}
      aria-labelledby="modal-danfe-title"
      centered
      dialogClassName="custom-modal"
    >
      <Modal.Header closeButton style={headerStyle}>
        <Modal.Title id="modal-danfe-title">DANFE</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <ContainerPdf>
          <div style={progressContainerStyle}>
            {loadingPDF && <LinearProgress style={progressStyle} />}
          </div>

          <LeitorPDF />
        </ContainerPdf>
      </Modal.Body>

      <Modal.Footer style={footerStyle}>
        <div className="actions">
          <div className="left">
            <Button
              className="btn-impressao"
              onClick={handlePrint}
              title="Imprimir PDF"
              aria-label="Imprimir PDF"
              disabled={!hasPdf || loadingPDF}
            >
              Imprimir
              <AiOutlinePrinter size={24} className="btn-icon" />
            </Button>

            <Button
              onClick={handleDownload}
              title="Baixar PDF"
              aria-label="Baixar PDF"
              disabled={!hasPdf || loadingPDF}
            >
              Download
              <AiOutlineDownload size={24} className="btn-icon" />
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </TransmitModal>
  );
};
